import { Injectable, inject } from '@angular/core'
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { switchMap, map, catchError, of, filter } from 'rxjs'
import {
	ConfirmReservationDto,
	OrderGateway,
	ResponseAvailabilityReservation,
} from '../../gateways/order.gateway'
import { ReservationActions } from './reservation.actions'
import { getClient } from '../client/client.selectors'
import { getOrderIdAndVenueId, getConfirmReservation, getSections } from './reservation.selectors'
import { Section } from './reservation'
import { DataLayerService, PartnersId, PartnerToken } from '@monorepo-channels/shared/util-helpers'

@Injectable()
export class ReservationEffects {
	private actions$ = inject(Actions)
	private orderGateway = inject(OrderGateway)
	private store = inject(Store)
	private dataLayerService = inject(DataLayerService)
	private partnerId = inject(PartnerToken)

	setScheduleFromPartysize$ = createEffect(() => {
		return this.actions$.pipe(
			filter(() => ([PartnersId.BRADESCO] as string[]).includes(this.partnerId)),
			ofType(ReservationActions.setSelectedSection),
			concatLatestFrom(() => this.store.select(getSections)),
			map(([action, sections]) => {
				const section = sections?.find(sc => sc.label === action.selectedSectionLabel) as Section
				return ReservationActions.setScheduleFromPartysize({
					selectedPartySize: String(section.schedules[0].partySize),
				})
			})
		)
	})

	loadAvailabilities$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(ReservationActions.loadAvailabilities),
			concatLatestFrom(() => this.store.select(getClient)),
			switchMap(([action, client]) => {
				return this.orderGateway
					.checkAvailability({
						venueId: action.venueId,
						voucherId: action.voucherId,
						clientId: client?.id as string,
						categoryId: client?.selectedCategory?.categoryId as string,
						intent: 'reservation',
					})
					.pipe(
						switchMap((response: unknown) => {
							const correctResponse = response as ResponseAvailabilityReservation
							this.dataLayerService.logEvent({
								event: 'CH_reservation_load_availabilities_success',
								clientId: client?.id,
								venueId: action.venueId,
							})

							return [
								ReservationActions.loadAvailabilitiesSuccess({
									availabilities: correctResponse.availabilities,
								}),
								ReservationActions.setOrderIdAfterLoadingAvailabilities({
									orderId: correctResponse.order._id,
									venueId: action.venueId,
								}),
							]
						}),
						catchError(error => {
							this.dataLayerService.logEvent({
								event: 'CH_reservation_load_availabilities_error',
								clientId: client?.id,
								venueId: action.venueId,
								error: error?.error?.message,
							})
							return of(
								ReservationActions.loadAvailabilitiesFail({
									error: error?.error?.message,
								})
							)
						})
					)
			})
		)
	})

	confirmReservation$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(ReservationActions.confirmReservation),
			concatLatestFrom(() => [
				this.store.select(getOrderIdAndVenueId),
				this.store.select(getConfirmReservation),
				this.store.select(getClient),
			]),
			switchMap(([, order, confirmReservationState, client]) =>
				this.orderGateway
					.confirmOrder(order.orderId!, {
						partySize: confirmReservationState.partySize,
						reservationDay: confirmReservationState.reservationDay,
						reservationTime: confirmReservationState.reservationTime,
						section: confirmReservationState.sectionId,
						sectionLabel: confirmReservationState.sectionLabel,
						clientId: client?.id,
					} as ConfirmReservationDto)
					.pipe(
						switchMap(({ voucher }) => {
							this.dataLayerService.logEvent({
								event: 'CH_reservation-success',
								clientId: client?.id,
								reservationDay: confirmReservationState.reservationDay,
								reservationTime: confirmReservationState.reservationTime,
								venue: order.venueId,
								orderId: order.orderId,
							})
							if (voucher?.value) {
								return [
									ReservationActions.setVoucherValueAfterReservationConfirm({
										voucherValue: voucher.value,
									}),
									ReservationActions.confirmReservationSuccess(),
								]
							}
							return [ReservationActions.confirmReservationSuccess()]
						}),
						catchError(error =>
							of(
								ReservationActions.confirmReservationFail({
									error: error?.error?.message,
								})
							)
						)
					)
			)
		)
	})
}
